import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { X, Plus } from 'lucide-react';

const MultiTeacherSelect = ({ teachers = [], selectedTeacherIds = [], onChange, dataTestId }) => {
  const [tempSelectedId, setTempSelectedId] = useState('');
  
  // Ensure we always have valid arrays
  const safeTeachers = Array.isArray(teachers) ? teachers : [];
  const safeSelectedIds = Array.isArray(selectedTeacherIds) ? selectedTeacherIds : [];

  const addTeacher = () => {
    if (tempSelectedId && !safeSelectedIds.includes(tempSelectedId)) {
      onChange([...safeSelectedIds, tempSelectedId]);
      setTempSelectedId('');
    }
  };

  const removeTeacher = (teacherId) => {
    onChange(safeSelectedIds.filter(id => id !== teacherId));
  };

  const getTeacherName = (teacherId) => {
    const teacher = safeTeachers.find(t => t && t.id === teacherId);
    return teacher ? `${teacher.name} ${teacher.surname}` : 'Unknown';
  };

  const availableTeachers = safeTeachers.filter(teacher => 
    teacher && !safeSelectedIds.includes(teacher.id)
  );

  return (
    <div className="space-y-3" data-testid={dataTestId}>
      {/* Selected Teachers */}
      {safeSelectedIds.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="selected-teachers">
          {safeSelectedIds.map((teacherId) => {
            // Ensure teacherId is a string
            const teacherIdStr = String(teacherId);
            return (
              <Badge
                key={teacherIdStr}
                variant="secondary"
                className="flex items-center space-x-1 px-2 py-1"
                data-testid={`selected-teacher-${teacherIdStr}`}
              >
                <span>{getTeacherName(teacherIdStr)}</span>
                <button
                  type="button"
                  onClick={() => removeTeacher(teacherIdStr)}
                  className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
                  data-testid={`remove-teacher-${teacherIdStr}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}

      {/* Add Teacher */}
      {availableTeachers.length > 0 && (
        <div className="flex space-x-2">
          <Select value={tempSelectedId} onValueChange={(value) => {
            setTempSelectedId(value);
            // Auto-add teacher when selected for better UX
            if (value && !safeSelectedIds.includes(value)) {
              onChange([...safeSelectedIds, value]);
              setTempSelectedId('');
            }
          }} data-testid="teacher-select">
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Seleziona insegnante da aggiungere" />
            </SelectTrigger>
            <SelectContent>
              {availableTeachers.map((teacher) => (
                <SelectItem key={teacher.id} value={teacher.id} data-testid={`teacher-option-${teacher.id}`}>
                  {teacher.name} {teacher.surname}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {availableTeachers.length === 0 && safeSelectedIds.length === 0 && (
        <p className="text-sm text-gray-500" data-testid="no-teachers">
          Nessun insegnante disponibile
        </p>
      )}
    </div>
  );
};

export default MultiTeacherSelect;